import React, { useState, useEffect } from "react";
import { Loading } from "@empirica/core/player/react";
import { useGame, usePlayer } from "@empirica/core/player/classic/react";
import {
  getCompensationConfig,
  isBaseRateStaticSyntheticGroupCondition,
} from "../utils/conditionUtils.js";

const culculateRemainingTime = (preLobbyCompletedAt) => {
  if (!preLobbyCompletedAt) {
    return 600; // 10 minutes in seconds
  }
  const elapsed = Math.floor((Date.now() - new Date(preLobbyCompletedAt)) / 1000);
  return Math.max(600 - elapsed, 0); // 600 seconds = 10 minutes
};

export default function MyLobby() {
  const player = usePlayer();
  const game = useGame();
  const [remainingTime, setRemainingTime] = useState(culculateRemainingTime(player.get("preLobbyCompletedAt"))); // 10 minutes in seconds

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const preLobbyCompletedAt = player.get("preLobbyCompletedAt");
      if (!preLobbyCompletedAt) {
        return;
      }
      const remaining = culculateRemainingTime(preLobbyCompletedAt);
      setRemainingTime(remaining);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  console.log("MyLobby rendered");

  if (!player) {
    return <Loading />;
  }

  const treatment = player.get("treatment") || game?.get("treatment") || {};
  const compensation = getCompensationConfig(treatment || {});
  const isBaseRateStaticSyntheticGroup =
    isBaseRateStaticSyntheticGroupCondition(treatment || {});
  const playerCount = treatment.playerCount || treatment.factors?.playerCount;

  if (!treatment || !playerCount) {
    return <Loading />;
  }

  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 640 512"
          className="mx-auto h-12 w-12 text-gray-400"
          stroke="none"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M544 224c44.2 0 80-35.8 80-80s-35.8-80-80-80-80 35.8-80 80 35.8 80 80 80zm0-128c26.5 0 48 21.5 48 48s-21.5 48-48 48-48-21.5-48-48 21.5-48 48-48zM320 256c61.9 0 112-50.1 112-112S381.9 32 320 32 208 82.1 208 144s50.1 112 112 112zm0-192c44.1 0 80 35.9 80 80s-35.9 80-80 80-80-35.9-80-80 35.9-80 80-80zm244 192h-40c-15.2 0-29.3 4.8-41.1 12.9 9.4 6.4 17.9 13.9 25.4 22.4 4.9-2.1 10.2-3.3 15.7-3.3h40c24.2 0 44 21.5 44 48 0 8.8 7.2 16 16 16s16-7.2 16-16c0-44.1-34.1-80-76-80zM96 224c44.2 0 80-35.8 80-80s-35.8-80-80-80-80 35.8-80 80 35.8 80 80 80zm0-128c26.5 0 48 21.5 48 48s-21.5 48-48 48-48-21.5-48-48 21.5-48 48-48zm304.1 180c-33.4 0-41.7 12-80.1 12-38.4 0-46.7-12-80.1-12-36.3 0-71.6 16.2-92.3 46.9-12.4 18.4-19.6 40.5-19.6 64.3V432c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48v-44.8c0-23.8-7.2-45.9-19.6-64.3-20.7-30.7-56-46.9-92.3-46.9zM480 432c0 8.8-7.2 16-16 16H176c-8.8 0-16-7.2-16-16v-44.8c0-16.6 4.9-32.7 14.1-46.4 13.8-20.5 38.4-32.8 65.7-32.8 27.4 0 37.2 12 80.2 12s52.8-12 80.1-12c27.3 0 51.9 12.3 65.7 32.8 9.2 13.7 14.1 29.8 14.1 46.4V432zM157.1 268.9c-11.9-8.1-26-12.9-41.1-12.9H76c-41.9 0-76 35.9-76 80 0 8.8 7.2 16 16 16s16-7.2 16-16c0-26.5 19.8-48 44-48h40c5.5 0 10.8 1.2 15.7 3.3 7.5-8.5 16.1-16 25.4-22.4z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          {isBaseRateStaticSyntheticGroup
            ? "Preparing your group decision task"
            : playerCount > 1
            ? "Waiting for other players"
            : "Game loading"}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Please wait for the game to be ready.
        </p>
        {isBaseRateStaticSyntheticGroup ? (
          <p>
            We are preparing the other participants' responses for your review.
          </p>
        ) : (
          <>
            <p>
              We are waiting for other participants to join the game. Maximum
              waiting time: 10 minutes. <br /> After that, you will be directed to
              the exit survey and receive the {compensation.basePaymentAmountText}{" "}
              base payment upon completing the survey.
            </p>
            <p>
              Time remaining: {formatTime(remainingTime)}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
